// src/components/AdminUsersScreen.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import './AdminUsersScreen.css';
import { adminApi } from '../services/adminApi';

function AdminUsersScreen({ getAccessTokenSilently }) {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [perPage] = useState(20);

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  const [selectedRolesByUser, setSelectedRolesByUser] = useState({});

  const hasAcceptedRole = useCallback(function (u) {
    const userRoles = u && u.roles;
    if (!Array.isArray(userRoles)) return false;
    return userRoles.some(function (roleObj) {
      const roleName = roleObj.name || '';
      return roleName.toLowerCase() === 'accepted';
    });
  }, []);

  const loadUsers = useCallback(async function () {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const data = await adminApi.listUsers({ token, page, per_page: perPage, q });
      const list = Array.isArray(data && data.users) ? data.users : [];
      setUsers(list);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, page, perPage, q]);

  const loadRoles = useCallback(async function () {
    setRolesLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const data = await adminApi.listRoles({ token });
      const raw = Array.isArray(data && data.roles) ? data.roles : [];

      const normalized = raw.map(function (r) {
        const nid =
          (r && r.id) != null ? r.id :
          (r && r.role_id) != null ? r.role_id :
          (r && r._id) != null ? r._id :
          (r && r.value) != null ? r.value :
          null;

        const nname =
          (r && r.name) != null ? r.name :
          (r && r.display_name) != null ? r.display_name :
          String(nid || '');

        return Object.assign({}, r, { id: nid, name: nname });
      });

      setRoles(normalized);
    } catch (e) {
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(function () { loadUsers(); }, [loadUsers]);
  useEffect(function () { loadRoles(); }, [loadRoles]);

  // Show only users WITHOUT "Accepted" role
  const filteredUsers = useMemo(function () {
    return users.filter(function (u) {
      return !hasAcceptedRole(u);
    });
  }, [users, hasAcceptedRole]);

  const handleToggleRole = useCallback(function (user_id, role_id) {
    setSelectedRolesByUser(function (prev) {
      const current = prev[user_id] || [];
      const isSelected = current.includes(role_id);
      if (isSelected) {
        return { ...prev, [user_id]: current.filter(function (id) { return id !== role_id; }) };
      } else {
        return { ...prev, [user_id]: [...current, role_id] };
      }
    });
  }, []);

  const handleAssignRoles = useCallback(async function (user_id) {
    const roleIds = selectedRolesByUser[user_id] || [];
    if (roleIds.length === 0) return;

    try {
      const token = await getAccessTokenSilently();
      await adminApi.assignRoles({ token, user_id, role_ids: roleIds });
      await loadUsers();
      setSelectedRolesByUser(function (prev) {
        const newState = { ...prev };
        delete newState[user_id];
        return newState;
      });
    } catch (e) {
    }
  }, [getAccessTokenSilently, loadUsers, selectedRolesByUser]);

  const handleRemoveRole = useCallback(async function (user_id, role_id) {
    try {
      const token = await getAccessTokenSilently();
      await adminApi.removeRoles({ token, user_id, role_ids: [role_id] });
      await loadUsers();
    } catch (e) {
    }
  }, [getAccessTokenSilently, loadUsers]);

  const getUserRoles = useCallback(function (u) {
    const userRoles = u && u.roles;
    return Array.isArray(userRoles) ? userRoles : [];
  }, []);

  return (
    <div className="admin-users-screen">
      <div className="admin-toolbar">
        <h2>User Administration (Auth0)</h2>
        <div className="admin-search">
          <input
            type="search"
            placeholder="Search by email, name, etc."
            value={q}
            onChange={function (e) { setPage(0); setQ(e.target.value); }}
          />
        </div>
      </div>

      {loading && <div className="admin-loading">Loading users...</div>}
      {error && <div className="admin-error">Error: {error.message}</div>}

      {!loading && !error && (
        <>
          {filteredUsers.length === 0 ? (
            <div className="admin-empty">No users left to accept.</div>
          ) : (
            <div className="admin-users-table-wrapper full-width">
              <div className="admin-users-table-scroller">
                <table className="admin-users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Contact Email</th>
                      <th>Agency</th>
                      <th>Current Roles</th>
                      <th>Assign Roles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(function (u) {
                      const app = (u && u.app_metadata) || {};
                      const userMeta = (u && u.user_metadata) || {};

                      // Name preference: user_metadata.first_name > u.name > "first last" > "-"
                      const metaFirst = userMeta?.first_name;
                      const metaLast = userMeta?.last_name;
                      const name =
                        (metaFirst && String(metaFirst)) ||
                        (u.name && String(u.name)) ||
                        (metaFirst && metaLast ? (String(metaFirst) + ' ' + String(metaLast)) : '-') ||
                        '-';

                      const contactEmail = u.email || '-';
                      const agency = app.organization || userMeta.organization || '-';

                      const userRoles = getUserRoles(u);
                      const selectedRoles = selectedRolesByUser[u.user_id] || [];

                      // Exclude already assigned roles from dropdown options
                      const assignedIds = new Set(userRoles.map(r => r.id));
                      const availableRoles = roles.filter(r => !assignedIds.has(r.id));

                      return (
                        <tr key={u.user_id}>
                          <td>{name}</td>
                          <td>{contactEmail}</td>
                          <td>{agency}</td>
                          <td>
                            <div className="current-roles">
                              {userRoles.length === 0 ? (
                                <span className="no-roles">No roles assigned</span>
                              ) : (
                                userRoles.map(function (roleObj) {
                                  const roleId = roleObj.id;
                                  const roleName = roleObj.name || roleId;

                                  return (
                                    <span key={roleId} className="role-badge">
                                      {roleName}
                                      <button
                                        className="remove-role-btn"
                                        onClick={function () { handleRemoveRole(u.user_id, roleId); }}
                                        title="Remove role"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  );
                                })
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="assign-roles-cell">
                              <MultiSelectDropdown
                                options={availableRoles}
                                loading={rolesLoading}
                                selected={selectedRoles}
                                onToggle={(roleId) => handleToggleRole(u.user_id, roleId)}
                                placeholder="Search and select roles..."
                                labelKey="name"
                                valueKey="id"
                              />
                              <button
                                className="btn-assign-roles"
                                onClick={function () { handleAssignRoles(u.user_id); }}
                                disabled={rolesLoading || selectedRoles.length === 0}
                              >
                                Assign Roles ({selectedRoles.length})
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="admin-pagination">
            <button onClick={function () { setPage(function (p) { return Math.max(p - 1, 0); }); }} disabled={page === 0}>Previous</button>
            <span>Page {page + 1}</span>
            <button onClick={function () { setPage(function (p) { return p + 1; }); }} disabled={users.length < perPage}>Next</button>
          </div>
        </>
      )}
    </div>
  );
}

AdminUsersScreen.propTypes = {
  getAccessTokenSilently: PropTypes.func.isRequired,
};

export default AdminUsersScreen;

const useIdOrUndefined = typeof React.useId === 'function' ? React.useId : () => undefined;

function useStableId(prefix = 'msd') {
  const autoId = useIdOrUndefined();
  const ref = React.useRef(null);

  if (ref.current == null) {
    const rnd = Math.random().toString(36).slice(2);
    ref.current = `${prefix}-${rnd}`;
  }

  return autoId ? `${ref.current}-${autoId}` : ref.current;
}

/* Searchable multi-select dropdown*/
function MultiSelectDropdown({
  options,
  selected,
  onToggle,
  loading = false,
  placeholder = 'Search...',
  labelKey = 'name',
  valueKey = 'id',
  noResultsText = 'No results',
  maxDropdownHeight = 240,
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [highlightIndex, setHighlightIndex] = React.useState(0);
  const containerRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const listboxId = useStableId('msd-listbox');

  const selectedSet = React.useMemo(() => new Set(selected || []), [selected]);

  const filtered = React.useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter((opt) => {
      const label = String(opt?.[labelKey] ?? '').toLowerCase();
      const idStr = String(opt?.[valueKey] ?? '').toLowerCase();
      return label.includes(q) || idStr.includes(q);
    });
  }, [options, query, labelKey, valueKey]);

  const activeId = React.useMemo(() => {
    return open && (filtered.length > 0)
      ? `${listboxId}-opt-${highlightIndex}`
      : undefined;
  }, [open, filtered.length, highlightIndex, listboxId]);

  React.useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery('');
      setHighlightIndex(0);
    }
  }, [open]);

  React.useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOpen = () => setOpen((v) => !v);

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[highlightIndex];
      if (item) onToggle(item[valueKey]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const removeChip = (id) => {
    onToggle(id);
  };

  const selectedItems = React.useMemo(() => {
    const byId = new Map((options || []).map((o) => [o[valueKey], o]));
    return (selected || []).map((id) => byId.get(id)).filter(Boolean);
  }, [selected, options, valueKey]);

  return (
    <div className="msd-container" ref={containerRef}>
      <div
        className={`msd-control ${open ? 'open' : ''}`}
        onClick={toggleOpen}
        role="combobox"
        tabIndex={0}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-activedescendant={activeId || undefined}
        onKeyDown={handleKeyDown}
      >
        <div className="msd-chips">
          {selectedItems.length === 0 && (
            <span className="msd-placeholder">{placeholder}</span>
          )}
          {selectedItems.map((item) => (
            <span key={item[valueKey]} className="msd-chip">
              {item[labelKey]}
              <button
                className="msd-chip-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  removeChip(item[valueKey]);
                }}
                aria-label={`Remove ${item[labelKey]}`}
                title="Remove"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <span className="msd-caret">▾</span>
      </div>

      {open && (
        <div
          className="msd-menu"
          role="listbox"
          id={listboxId}
          onKeyDown={handleKeyDown}
        >
          <div className="msd-search">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlightIndex(0);
              }}
              placeholder="Search..."
              className="msd-search-input"
              onKeyDown={handleKeyDown}
              aria-label="Search roles"
            />
          </div>

          <div
            className="msd-options"
            style={{ maxHeight: maxDropdownHeight, overflowY: 'auto' }}
          >
            {loading ? (
              <div className="msd-option disabled">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="msd-option disabled">{noResultsText}</div>
            ) : (
              filtered.map((opt, idx) => {
                const id = opt[valueKey];
                const label = opt[labelKey];
                const checked = selectedSet.has(id);
                const highlighted = idx === highlightIndex;
                return (
                  <div
                    key={id}
                    id={`${listboxId}-opt-${idx}`}
                    role="option"
                    aria-selected={checked}
                    className={`msd-option ${highlighted ? 'highlight' : ''}`}
                    onMouseEnter={() => setHighlightIndex(idx)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onToggle(id);
                    }}
                  >
                    <input type="checkbox" readOnly checked={checked} />
                    <span className="msd-option-label">{label}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}